import React, { useState, FC, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Coffee, PlayCircle, ChevronDown, PhoneIncoming, PhoneOutgoing, Timer,
  LogOut, ArrowRight, ShieldCheck, Clock, Layers, PhoneCall, RotateCcw, 
  Activity, X, CheckCircle, XCircle, PhoneForwarded, AlertTriangle 
} from 'lucide-react';
import axios from 'axios';
import mqtt, { MqttClient } from 'mqtt';
import {ListWrapper} from '../../modules/apps/ticket/components/ticket-list/List';
import { ListWrapper as RecodingList} from '../../modules/apps/call/recording/recording-outbound-list/List';
import { CampaignDetailInfo } from '../../modules/apps/call/campaign-list/form/CampaignDetailInfo'; 
import {useQuery} from 'react-query'
import {useThemeMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import { CustomDatePicker } from '../../modules/widgets/components/Datepicker';

const AgentDashboard: FC = () => {
  useEffect(() => {
    document.body.setAttribute('data-kt-app-sidebar-minimize', 'on');
    return () => {
      document.body.removeAttribute('data-kt-app-sidebar-minimize');
    };
  }, []);

  const getApiToken = () => {
    try {
      const raw = localStorage.getItem('kt-auth-react-v');
      if (!raw) return { token: '', ext: '', firstName: '' };
      const parsed = JSON.parse(raw);
      return { 
        token: parsed.api_token || '', 
        ext: parsed.extension || '',
        firstName: parsed.first_name || ''
      };
    } catch { return { token: '', ext: '', firstName: '' }; }
  };

  const { token, ext: agentExt, firstName } = getApiToken();
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const getDateStorageKey = () => {
    const userKey = firstName ? firstName.toLowerCase().replace(/\s+/g, '_') : (agentExt || 'default');
    return `agent_dashboard_date_range_${userKey}`;
  };

  const getFilterStorageKey = () => {
    const userKey = firstName ? firstName.toLowerCase().replace(/\s+/g, '_') : (agentExt || 'default');
    return `recording_outbound_filter_${userKey}`;
  };

  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(() => {
    try {
      const saved = localStorage.getItem(getDateStorageKey());
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          startDate: parsed.startDate ? new Date(parsed.startDate) : new Date(),
          endDate: parsed.endDate ? new Date(parsed.endDate) : new Date(),
        };
      }
    } catch (err) {
      console.error('Failed to load dateRange from localStorage:', err);
    }
    return {
      startDate: new Date(),
      endDate: new Date(),
    };
  });

  useEffect(() => {
    try {
      const toSave = {
        startDate: dateRange.startDate ? dateRange.startDate.toISOString() : null,
        endDate: dateRange.endDate ? dateRange.endDate.toISOString() : null,
      };
      localStorage.setItem(getDateStorageKey(), JSON.stringify(toSave));
    } catch (err) {
      console.error('Failed to save dateRange to localStorage:', err);
    }
  }, [dateRange, firstName, agentExt]);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  const endpoint = `${apiUrl}/dashboard/agent-stats`;

  const {mode} = useThemeMode()
  const [triggerPopData, setTriggerPopData] = useState<{phone: string, timestamp: number} | null>(null);
  const lastStatusRef = useRef<string>("");

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isAux, setIsAux] = useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] = useState('Select Campaign');
  
  const isDark = mode === 'dark' || document.documentElement.getAttribute('data-bs-theme') === 'dark'
  const [activeTicket, setActiveTicket] = useState<any>(null);

  const [auxReasons, setAuxReasons] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);

  const [data, setData] = useState<any>({
    stats: { 
      outbound: 0, 
      outboundAnswered: 0,
      outboundNoAnswer: 0,
      outboundBusy: 0,
      outboundFailed: 0,
      serviceLevel: '0%', 
      avgTalkTime: '00:00' 
    },
    queue: [],
    performance: { current: 0, target: 100 },
    abandoned: []
  });

  const [showAuxMenu, setShowAuxMenu] = useState(false);
  const [showCampaignMenu, setShowCampaignMenu] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
  
  const esRef = useRef<EventSource | null>(null);
  const clientRef = useRef<MqttClient | null>(null); 
  const auxMenuRef = useRef<HTMLDivElement>(null);
  const campaignMenuRef = useRef<HTMLDivElement>(null);

  const clearTrigger = useCallback(() => {
    setTriggerPopData(null);
  }, []);

  const { isLoading: isValidatingAgent } = useQuery(
    ['verify-agent', agentExt],
    async () => {
      const res = await axios.get(`${apiUrl}/dashboard/verify`, config);
      return res.data;
    },
    {
      enabled: !!token && !!agentExt,
      refetchOnWindowFocus: false,
      staleTime: 0, 
      onSuccess: (resData) => {
        if (resData.authenticated && resData.isLoggedIn) {
          setIsLoggedIn(true);
          setIsAux(resData.isAux);
          setSelectedCampaign(resData.campaignName || 'Select Campaign');
          if (resData.loginAt) {
            localStorage.setItem('agent_login_at', resData.loginAt);
          }
        } else {
          setIsLoggedIn(false);
          setIsAux(false);
        }
      },
      onError: () => {
        setIsLoggedIn(false);
        setIsAux(false);
      }
    }
  );

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [resAux, resCamp] = await Promise.all([
          axios.get(`${apiUrl}/dashboard/aux-reasons`, config),
          axios.get(`${apiUrl}/dashboard/campaigns`, config)
        ]);

        setAuxReasons(resAux.data);

        const outboundOption = { id: 0, name: "outbound" }; 
        const updatedCampaigns = [outboundOption, ...resCamp.data];
        
        setCampaigns(updatedCampaigns);
      } catch (err) {
        console.error("Failed to fetch metadata:", err);
      }
    };
    fetchMetadata();
  }, []);
  
  const activeCampaignData = useMemo(() => {
    return campaigns.find(c => c.name === selectedCampaign && c.name !== 'outbound');
  }, [selectedCampaign, campaigns]);
  const { data: fullCampaignDetail, isLoading: isLoadingDetail } = useQuery(
    ['campaign-detail', activeCampaignData?.id],
    async () => {
      const response = await axios.get(`${apiUrl}/call/campaign/${activeCampaignData?.id}`, config);
      return response.data; 
    },
    {
      enabled: !!activeCampaignData?.id, 
      refetchOnWindowFocus: false,
      staleTime: 300000, 
    }
  );
  
  useEffect(() => {
    const MQTT_URL = process.env.REACT_APP_MQTT_URL;
    if (isLoggedIn && agentExt) {
      const client = mqtt.connect(`wss://${MQTT_URL}`, {
        clientId: `AgentDash_${agentExt}_${Math.random().toString(16).slice(2, 8)}`,
        username: 'user_asterisk',
        password: 'password_rahasia',
        clean: true,
        reconnectPeriod: 2000,
      });

      clientRef.current = client;

      client.on('connect', () => {
        client.subscribe(`dashboard/ext_status/${agentExt}`);
      });

      client.on('message', (_topic, payload) => {
          try {
            const rawData = JSON.parse(payload.toString());
            const currentStatus = rawData.status; 
            const currentCaller = rawData.caller; 
            const prevStatus = lastStatusRef.current;
            if (currentStatus === "UP" && prevStatus !== "UP" && currentCaller) {
              setTriggerPopData({ 
                phone: currentCaller, 
                timestamp: Date.now() 
              });
              setActiveTicket(null);
            }

            if (currentStatus === "RINGING" && prevStatus !== "RINGING" && currentCaller) {
              setActiveTicket({
                callerNumber: currentCaller,
                display: rawData.display || 'Incoming Call'
              });
            }

            if (currentStatus === "IDLE" && prevStatus !== "IDLE") {
              setActiveTicket(null);
            }

            lastStatusRef.current = currentStatus;

          } catch (err) {
            console.error('MQTT Parsing Error', err);
          }
      });

      return () => { if (client) client.end(true); };
    }
  }, [isLoggedIn, agentExt]);
  

  const handleLogin = async () => {
    if (selectedCampaign === 'Select Campaign') return alert("Pilih Campaign dulu!");
    try {
      const res = await axios.post(`${apiUrl}/dashboard/login`, { campaignName: selectedCampaign }, config);
      if (res.data.success) {
        localStorage.removeItem(getFilterStorageKey());
        localStorage.removeItem(getDateStorageKey());
        
        setIsLoggedIn(true);
        connect();
        window.location.reload();
      }
    } catch (err) { alert("Login Gagal!"); }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${apiUrl}/dashboard/logout`, {}, config);
    } finally {
      setIsLoggedIn(false);
      setIsAux(false);
      localStorage.removeItem('agent_login_at');
      localStorage.removeItem(getFilterStorageKey());
      localStorage.removeItem(getDateStorageKey());
      
      cleanup();
      window.location.reload();
    }
  };

  const handleAuxAction = async (id: number | null) => {
    const isStarting = id !== null;
    const url = isStarting ? `${apiUrl}/dashboard/aux/start` : `${apiUrl}/dashboard/aux/stop`;
    try {
      await axios.post(url, isStarting ? { auxReasonId: id } : {}, config);
      setIsAux(isStarting);
      localStorage.setItem('agent_is_aux', isStarting.toString());
      setShowAuxMenu(false);
    } catch (err) { console.error("AUX Error", err); }
  };

  const connect = () => {
    cleanup();
    if (!isLoggedIn) return;

    const startStr = formatDate(dateRange.startDate);
    const endStr = formatDate(dateRange.endDate);

    let sseUrl = `${endpoint}${endpoint.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
    if (startStr) sseUrl += `&startDate=${startStr}`;
    if (endStr) sseUrl += `&endDate=${endStr}`;

    const es = new EventSource(sseUrl);
    esRef.current = es;
    es.onopen = () => setConnectionStatus('connected');
    es.onmessage = (e) => setData(JSON.parse(e.data));
    es.onerror = () => setConnectionStatus('disconnected');
  };

  const cleanup = () => { esRef.current?.close(); setConnectionStatus('disconnected'); };

  useEffect(() => {
    if (isLoggedIn) {
      const syncTime = () => {
        const loginAt = localStorage.getItem('agent_login_at');
        if (loginAt) {
          const startTime = new Date(loginAt).getTime();
          const now = new Date().getTime();
          const diffInSeconds = Math.floor((now - startTime) / 1000);
          
          setSeconds(diffInSeconds > 0 ? diffInSeconds : 0);
        }
      };

      syncTime();
      connect();
    }
    return () => cleanup();
  }, [isLoggedIn, dateRange]);

  useEffect(() => {
    let interval: any;
    if (isLoggedIn && !isAux) interval = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isLoggedIn, isAux]);

  const formatDuration = (totalSeconds) => {
    if (!totalSeconds) return '00:00:00';
    const hrs = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
    const mins = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
    const secs = String(Math.floor(totalSeconds % 60)).padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  const formatDate = (d: Date | null) => {
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatDurationValue = (value: any) => {
    if (!value) return '00:00:00';
    if (typeof value === 'string') return value;
    return formatDuration(value);
  };

  const ScreenPop = () => {
    if (!activeTicket) return null;
    return (
      <div className="position-fixed bottom-0 end-0 m-10 animate__animated animate__headShake animate__infinite animate__slow" style={{ zIndex: 9999, width: '350px' }}>
        <div className="card shadow-lg border-0 border-top border-primary border-4 bg-body shadow-lg">
          <div className="card-body p-7">
            <div className="d-flex align-items-center mb-5">
              <div className="bg-light-primary p-3 rounded-circle me-4 animate__animated animate__pulse animate__infinite">
                <PhoneIncoming size={24} className="text-primary" />
              </div>
              <div className="flex-grow-1">
                <h4 className="fw-boldest m-0 text-gray-900 fs-5">Call Incoming</h4>
                <span className="text-muted fs-8 fw-bold">Extension: {agentExt}</span>
              </div>
              <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={() => setActiveTicket(null)}>
                <X size={18} />
              </button>
            </div>
            
            <div className="mb-6 text-center bg-light-secondary rounded p-4 border border-dashed">
              <span className="text-gray-500 fs-9 fw-boldest text-uppercase d-block mb-1">Customer Number</span>
              <h2 className="fw-boldest text-gray-800 m-0 letter-spacing-2">{activeTicket.callerNumber}</h2>
            </div>

          </div>
        </div>
      </div>
    );
  };

  const displayStats = data.stats;

  return (
    <div className="min-vh-100 w-100 bg-body">
      <ScreenPop />
      <nav className="navbar shadow-sm py-3 px-8 bg-body border-bottom border-secondary border-opacity-10">
        <div className="container-fluid p-0">
          <div className="d-flex align-items-center gap-3">
            <div className={`badge badge-circle w-10px h-10px ${connectionStatus === 'connected' ? 'bg-success' : 'bg-danger'}`}></div>
            <div className="position-relative" ref={campaignMenuRef}>
              <button 
                className={`btn d-flex align-items-center px-4 py-2 rounded-3 border-0 fs-7 fw-boldest shadow-sm ${isLoggedIn ? 'bg-secondary bg-opacity-10 text-muted' : 'bg-body text-primary border'}`}
                onClick={() => !isLoggedIn && setShowCampaignMenu(!showCampaignMenu)}
              >
                <Layers size={18} className="me-2 text-primary" />
                <span>{selectedCampaign}</span>
                {!isLoggedIn && <ChevronDown size={14} className="ms-2 opacity-50" />}
              </button>
              {showCampaignMenu && (
                <div className="position-absolute start-0 mt-2 card shadow-lg border-0 py-2 w-250px bg-body" style={{ zIndex: 10000 }}>
                  {campaigns.map(c => (
                    <div key={c.id} className="px-5 py-3 fs-7 fw-bold text-gray-700 hover-bg-light cursor-pointer" 
                         onClick={() => {setSelectedCampaign(c.name); setShowCampaignMenu(false);}}>
                      {c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {isLoggedIn && (
              <>
                <CustomDatePicker 
                  dateRange={dateRange} 
                  onDateChange={(dates: [Date | null, Date | null]) => {
                    const [start, end] = dates;
                    setDateRange({ startDate: start, endDate: end });
                  }} 
                  classCustom="form-control-sm"
                />
                
                <div className="bg-gray-900 text-gray-100 px-3 py-2 rounded-3 d-flex align-items-center me-2">
                  <Clock className="text-primary me-2" size={14} />
                  <span className="fs-7 fw-boldest font-monospace">{new Date(seconds * 1000).toISOString().substr(11, 8)}</span>
                </div>
              </>
            )}

            {!isLoggedIn ? (
              <button className="btn btn-primary btn-sm fw-boldest px-4 py-2" onClick={handleLogin}>LOGIN</button>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <div className="position-relative" ref={auxMenuRef}>
                  <button 
                    className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-3 fw-boldest border-0 ${isAux ? 'btn-warning text-white' : 'btn-success text-white'}`}
                    onClick={() => isAux ? handleAuxAction(null) : setShowAuxMenu(!showAuxMenu)}
                  >
                    {isAux ? <PlayCircle size={16} className="me-1"/> : <Coffee size={16} className="me-1"/>}
                    <span className="d-none d-md-inline">{isAux ? 'On Break' : 'Available'}</span>
                    <ChevronDown size={12} className="ms-2 opacity-50" />
                  </button>
                  {showAuxMenu && (
                    <div className="position-absolute end-0 mt-2 card shadow-lg border-0 py-2 w-180px bg-body" style={{ zIndex: 10000 }}>
                      {auxReasons.map(r => (
                        <button key={r.id} className="btn btn-active-light-primary text-gray-700 text-start fw-bold fs-7 py-3 px-5 border-0 bg-transparent w-100" 
                          onClick={() => handleAuxAction(r.id)}>
                          {r.reasonName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button className="btn btn-icon btn-sm btn-light-danger rounded-3" onClick={handleLogout}><LogOut size={18} /></button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="container-fluid p-6 p-md-8">  
        <div className="row g-5 mb-6 text-start align-items-stretch">
          <div className="col-12">
            <div className="row g-4">
              {[
                { l: 'Outbound Total', v: displayStats?.outbound, i: <PhoneOutgoing size={18}/>, color: '#3B82F6' }, 
                { l: 'Answered', v: displayStats?.outboundAnswered, i: <CheckCircle size={18}/>, color: '#10B981' }, 
                { l: 'No Answer', v: displayStats?.outboundNoAnswer, i: <XCircle size={18}/>, color: '#EF4444' }, 
                { l: 'Busy', v: displayStats?.outboundBusy, i: <PhoneForwarded size={18}/>, color: '#F59E0B' }, 
                { l: 'Failed', v: displayStats?.outboundFailed, i: <AlertTriangle size={18}/>, color: '#EF4444' },  
                { l: 'Duration', v: displayStats?.outboundDuration, i: <Clock size={18}/>, color: '#8B5CF6' },
              ].map((s, idx) => {
                const isDuration = s.l === 'Duration';
                const textColor = isDark ? '#f3f4f6' : '#1f2937';
                const subColor = isDark ? '#9ca3af' : '#6b7280';
                
                const gridClass = idx < 5 ? "col-12 col-sm-6 col-md-4 col-xl-2" : "col-12 col-sm-6 col-xl-2 flex-grow-1";

                return (
                  <div className={gridClass} key={idx}>
                    <div 
                      className="card border-0 h-100"
                      style={{
                        background: isDark 
                          ? `linear-gradient(135deg, rgba(31, 41, 55, 0.7) 0%, rgba(17, 24, 39, 0.7) 100%)` 
                          : '#ffffff',
                        borderRadius: '16px',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s',
                        border: isDark ? `1px solid rgba(255, 255, 255, 0.06)` : '1px solid rgba(0, 0, 0, 0.08)',
                        boxShadow: isDark 
                          ? '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.14)' 
                          : '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = isDark 
                          ? `0 12px 20px -8px ${s.color}44, 0 4px 12px rgba(0,0,0,0.3)` 
                          : `0 15px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)`; 
                        e.currentTarget.style.borderColor = `${s.color}44`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = isDark 
                          ? '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.14)' 
                          : '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.borderColor = isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)';
                      }}
                    >
                      <div className="card-body p-5 d-flex align-items-center">
                        <div 
                          className="p-3 rounded-3 me-4 d-flex align-items-center justify-content-center" 
                          style={{ 
                            backgroundColor: `${s.color}15`, 
                            color: s.color,
                            border: `1px solid ${s.color}20`,
                            minWidth: '42px',
                            minHeight: '42px'
                          }}
                        >
                          {s.i}
                        </div>
                        <div className="min-w-0">
                          <span className="fs-9 fw-bold text-uppercase d-block mb-1 tracking-wider text-truncate" style={{ color: subColor, fontSize: '0.68rem', letterSpacing: '0.05em' }}>
                            {s.l}
                          </span>
                          <span className={isDuration ? "fs-4 fw-black lh-1" : "fs-3 fw-black lh-1"} style={{ color: textColor, fontWeight: '800' }}>
                            {isDuration ? formatDurationValue(s.v) : (s.v ?? 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {isLoggedIn && (
          <div className="row">
            <div className="col-12" style={{ position: 'relative', zIndex: 2 }}>
              {!fullCampaignDetail || selectedCampaign === 'outbound' ? (
                <div 
                  className="card border-0"
                  style={{ 
                    borderRadius: '16px',
                    background: isDark ? 'rgba(31, 41, 55, 0.6)' : '#ffffff',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: isDark 
                      ? '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.14)' 
                      : '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <RecodingList dateRange={dateRange} />
                </div>
              ) : (
                <CampaignDetailInfo 
                  data={fullCampaignDetail} 
                  externalTicketTrigger={triggerPopData} 
                  onTriggerProcessed={clearTrigger}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDashboard;
